import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as DisabledIcon } from '@elementary/common-ui/assets/16x16/disabled.svg';
import { ReactComponent as Logo } from '@elementary/common-ui/assets/logo.svg';
import { ReactComponent as DeleteConnection } from '@elementary/common-ui/assets/minus-x-minus.svg';
import {
  Dialog,
  FlexCenter,
  FlexColumn,
  Link,
} from '@elementary/common-ui/components';
import {
  ALERT_RULES,
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  PRE_DELETE_INFO,
  SLACK,
  PAGER_DUTY,
  OPSGENIE,
  INCIDENT_IO,
  ROOTLY,
  TEAMS_WEBHOOKS,
  TEAMS_DEFAULTS,
  TEAMS_BOT,
} from '@elementary/common-ui/constants/queryKeys';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { IntegrationType, PreDeleteResponse } from 'shared/types';
import AlertDestinationIcon from 'shared/components/AlertDestinations/AlertDestinationIcon';
import { Alert, CircularProgress, Typography } from '@mui/material';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';

const IconsWrapper = styled(FlexCenter)`
  gap: ${space('sm')};
  height: 100%;
`;

const LogoWrapper = styled(FlexCenter)`
  width: 72px;
  height: 72px;
  border-radius: ${radius('xl')};
  padding: ${space('lg')};
  background: ${({ theme }) => theme.palette.accent.ai};
`;

const PlatformIconWrapper = styled(FlexCenter)`
  width: 72px;
  height: 72px;
  border-radius: ${radius('xl')};
  padding: ${space('lg')};
  border: 2px solid ${({ theme }) => theme.palette.border.default};
  box-sizing: border-box;
`;

const BlockingRulesList = styled.ul`
  margin: ${space('sm', 0, 0)};
  padding-left: ${space('xl')};
`;

const DestinationsList = styled(FlexColumn)`
  gap: ${space('xs')};
`;

const TopPartGraphic = ({
  integrationTypeKey,
}: {
  integrationTypeKey: IntegrationType;
}) => (
  <IconsWrapper>
    <LogoWrapper>
      <Logo />
    </LogoWrapper>
    <DeleteConnection />
    <PlatformIconWrapper>
      <AlertDestinationIcon type={integrationTypeKey} size={40} />
    </PlatformIconWrapper>
  </IconsWrapper>
);

const integrationTypeLabels: Record<IntegrationType, string> = {
  slack: 'Slack',
  microsoft_teams: 'Microsoft Teams',
  microsoft_teams_bot: 'Microsoft Teams (app)',
  pager_duty: 'PagerDuty',
  opsgenie: 'Opsgenie',
  incident_io: 'incident.io',
  rootly: 'Rootly',
  email: 'Email',
};

type Props = {
  open: boolean;
  onClose: () => void;
  integrationId: string | null;
  integrationName: string;
  integrationTypeKey: IntegrationType;
};

function DisconnectIntegrationDialog({
  open,
  onClose,
  integrationId,
  integrationName,
  integrationTypeKey,
}: Props) {
  const integrationType = integrationTypeLabels[integrationTypeKey];
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const preDeleteQuery = useQuery<PreDeleteResponse>(
    [PRE_DELETE_INFO, integrationId],
    () => api.fetchPreDeleteInfo(integrationId!),
    { enabled: open && !!integrationId, cacheTime: 0 },
  );

  useEffect(() => {
    if (open && integrationId) {
      preDeleteQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integrationId]);

  const disconnectMutation = useMutation(
    () => api.disconnectIntegration(integrationId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
        queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        queryClient.invalidateQueries([ALERT_RULES]);
        queryClient.invalidateQueries([SLACK]);
        queryClient.invalidateQueries([PAGER_DUTY]);
        queryClient.invalidateQueries([OPSGENIE]);
        queryClient.invalidateQueries([INCIDENT_IO]);
        queryClient.invalidateQueries([ROOTLY]);
        queryClient.invalidateQueries([TEAMS_WEBHOOKS]);
        queryClient.invalidateQueries([TEAMS_DEFAULTS]);
        queryClient.invalidateQueries([TEAMS_BOT]);
        openSnackbar({
          message: `${integrationName} disconnected successfully`,
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to disconnect integration',
          variant: 'error',
        });
      },
    },
  );

  const isLoading = preDeleteQuery.isLoading && !!integrationId;
  const isError = preDeleteQuery.isError;
  const data = preDeleteQuery.data;
  const canDelete = data?.can_delete ?? false;

  const envCount = new Set(
    data?.affected_destinations.map((dest) => dest.environment_name),
  ).size;
  const destCount = data?.affected_destinations.length ?? 0;

  type EnvData = {
    destinations: string[];
    ruleNames: Set<string>;
  };

  const initialDestMap: Record<string, EnvData> = {};
  const destinationsByEnv = (data?.affected_destinations ?? []).reduce(
    (acc, dest) => {
      const key = dest.environment_name || 'Unknown';
      if (!acc[key]) {
        acc[key] = { destinations: [], ruleNames: new Set() };
      }
      acc[key].destinations.push(`${dest.channel_name} (${dest.rule_name})`);
      acc[key].ruleNames.add(dest.rule_name);
      return acc;
    },
    initialDestMap,
  );

  return (
    <Dialog
      open={open}
      graphic={<TopPartGraphic integrationTypeKey={integrationTypeKey} />}
      titleIcon={<DisabledIcon />}
      title="Delete connection"
      variant="error"
      onClose={onClose}
      onConfirmButtonClick={() => disconnectMutation.mutate()}
      confirmText={
        disconnectMutation.isLoading ? 'Deleting' : 'Delete connection'
      }
      isLoading={disconnectMutation.isLoading}
      hideConfirmButton={isLoading || isError || !canDelete}
      confirmDisabled={disconnectMutation.isLoading}
    >
      {isLoading ? (
        <FlexCenter>
          <CircularProgress size={24} />
        </FlexCenter>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to load disconnect impact. Please close and try again.
        </Alert>
      ) : canDelete ? (
        <>
          <Typography>
            Are you sure you want to delete your{' '}
            <Typography
              component="span"
              sx={{ fontWeight: fontWeight('semibold') }}
            >
              {integrationType}
            </Typography>{' '}
            connection?
          </Typography>
          <Typography
            variant="body2"
            color="content.translucent.secondary"
            sx={{ mt: 1 }}
          >
            This will remove{' '}
            {pluralizeString(destCount, 'destination', 'destinations')} across{' '}
            {pluralizeString(envCount, 'environment', 'environments')}. This
            action is irreversible.
          </Typography>
          {integrationTypeKey === 'microsoft_teams_bot' && (
            <Typography
              variant="body2"
              color="content.translucent.secondary"
              sx={{ mt: 1 }}
            >
              The app will remain installed in Microsoft Teams — remove it there
              to fully uninstall.
            </Typography>
          )}
          {Object.keys(destinationsByEnv).length > 0 && (
            <DestinationsList>
              <Typography variant="body2" color="content.translucent.secondary">
                The following destinations will be removed:
              </Typography>
              {Object.entries(destinationsByEnv).map(([env, envData]) => {
                const ruleCount = envData.ruleNames.size;
                return (
                  <Typography key={env} variant="body2">
                    <strong>{env}</strong> (
                    {pluralizeString(
                      envData.destinations.length,
                      'destination',
                      'destinations',
                    )}{' '}
                    from{' '}
                    {pluralizeString(ruleCount, 'alert rule', 'alert rules')}
                    ): {envData.destinations.join(', ')}
                  </Typography>
                );
              })}
            </DestinationsList>
          )}
        </>
      ) : (
        <>
          <Typography>
            Cannot delete your{' '}
            <Typography
              component="span"
              sx={{ fontWeight: fontWeight('semibold') }}
            >
              {integrationType}
            </Typography>{' '}
            connection.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            The following alert rules would have no remaining destinations.
            Please edit or delete them first:
            <BlockingRulesList>
              {data?.blocking_rules.map((rule) => (
                <li key={rule.rule_id}>
                  <Link
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      onClose();
                      navigate(
                        `/${rule.environment_id}/alert-rules?rule_id=${rule.rule_id}`,
                      );
                    }}
                  >
                    &quot;{rule.rule_name}&quot; in {rule.environment_name}
                  </Link>
                </li>
              ))}
            </BlockingRulesList>
          </Alert>
        </>
      )}
    </Dialog>
  );
}

export default DisconnectIntegrationDialog;
